import type {
  Adjustment,
  Locale,
  PageSeo,
  Product,
} from "../interfaces/catalogProduct";
import type { LocationProduct } from "../context/ShoppingCartContext";
import type {
  LocationProductAdjustment,
  LocationProductForPricing,
  ResolvedLocationProductPricing,
} from "./locationProductAdjustments";
import {
  DEFAULT_SHOP_TIMEZONE,
  resolveLocationProductPricing,
} from "./locationProductAdjustments";
import { getPageLocaleUrl, resolveAbsoluteAssetUrl, stripPageRouteParam } from "./helpers";

type ProductPageLike = { languageCode?: string; url?: string };
type LanguageLike = { code?: string; languageCode?: string };

export function catalogAdjustmentToLocationAdjustment(
  adjustment: Adjustment
): LocationProductAdjustment {
  return {
    name: adjustment.name,
    value: adjustment.value,
    summarizeWithOtherAdjustments: adjustment.summarizeWithOtherAdjustments,
    timing: adjustment.timing,
  };
}

export function catalogProductToPricingInput(
  product: Product
): LocationProductForPricing {
  return {
    price_regular: product.priceRegular,
    price_regular_net: product.priceRegularNet,
    adjustments:
      product.adjustments?.map(catalogAdjustmentToLocationAdjustment) ?? null,
  };
}

export function resolveCatalogProductPricing(
  product: Product,
  date: Date = new Date(),
  timeZone: string = DEFAULT_SHOP_TIMEZONE
): ResolvedLocationProductPricing {
  return resolveLocationProductPricing(
    catalogProductToPricingInput(product),
    date,
    timeZone
  );
}

/** List/sort price: API `price` when present; otherwise client adjustment resolution. */
export function getCatalogProductListPrice(
  product: Product,
  date: Date = new Date(),
  timeZone: string = DEFAULT_SHOP_TIMEZONE
): number {
  if (!product.adjustments?.length) {
    return product.price;
  }

  const resolved = resolveCatalogProductPricing(product, date, timeZone);
  if (resolved.activeAdjustments.length > 0) {
    return resolved.price_total;
  }

  return product.price;
}

export function isListableCatalogProduct(product: Product): boolean {
  return !product.productOnlyVariation;
}

export function selectListableCatalogProducts(products: Product[]): Product[] {
  return products.filter(isListableCatalogProduct);
}

export function getCatalogProductTranslation(
  product: Product,
  locale: string
) {
  return (
    product.translations[locale as Locale] ??
    product.translations.et ??
    product.translations.en
  );
}

export function getCatalogProductDisplayFields(
  product: Product,
  locale: string
): Pick<
  ProductDisplayFields,
  | "displayName"
  | "shortDescription"
  | "description"
  | "deliveryInfo"
  | "firstFileUrl"
> {
  const translation = getCatalogProductTranslation(product, locale);
  const displayName = translation?.displayName || translation?.name || "";
  const shortDescription = translation?.shortDescription || "";
  const description = translation?.description || "";
  const deliveryInfo = translation?.deliveryInfo || "";
  const firstFileUrl =
    typeof product.firstFileUrl === "string" && product.firstFileUrl.trim()
      ? product.firstFileUrl.trim()
      : undefined;

  return {
    displayName,
    shortDescription,
    description,
    deliveryInfo,
    firstFileUrl,
  };
}

export function getCatalogProductImageSlides(
  product: Product
): { url: string }[] {
  const raw =
    product.imageUrls?.filter(
      (url): url is string => typeof url === "string" && url.trim() !== ""
    ) ??
    (typeof product.firstFileUrl === "string" && product.firstFileUrl.trim()
      ? [product.firstFileUrl.trim()]
      : []);

  return raw
    .map((url) => resolveAbsoluteAssetUrl(url) ?? url.trim())
    .filter((url): url is string => url !== "")
    .map((url) => ({ url }));
}

export function getCatalogProductDeliveryTimeLabel(
  product: Product,
  locale: string
): string {
  const days = product.deliveryTime?.days;
  if (days === undefined || days === null) {
    return "";
  }

  const onOrder = product.deliveryTimeSource === "onOrder";

  if (locale === "et") {
    return onOrder
      ? `Tarneaeg tellimisel: ${days} päeva`
      : `Tarneaeg laos: ${days} päeva`;
  }

  return onOrder
    ? `Delivery time when ordering: ${days} days`
    : `Delivery time from warehouse: ${days} days`;
}

export function getCatalogProductName(
  product: Product,
  locale: string
): string {
  return getCatalogProductTranslation(product, locale)?.name ?? "";
}

export function getCatalogProductSlug(
  product: Product,
  locale: string
): string | undefined {
  const slug = getCatalogProductTranslation(product, locale)?.productUrl;
  if (typeof slug !== "string") {
    return undefined;
  }
  const trimmed = slug.trim();
  return trimmed || undefined;
}

export function getLocationProductSlug(
  locationProduct: LocationProduct,
  locale: string
): string | undefined {
  const translations = locationProduct.translations;
  if (!translations || typeof translations !== "object") {
    return undefined;
  }

  const translation =
    translations[locale] ?? translations.et ?? translations.en;
  if (!translation || typeof translation !== "object") {
    return undefined;
  }

  const record = translation as Record<string, unknown>;
  const slug = record.product_url ?? record.productUrl;
  if (typeof slug !== "string") {
    return undefined;
  }
  const trimmed = slug.trim();
  return trimmed || undefined;
}

/**
 * URL path segment for product detail: catalog slug, location slug, then product UUID.
 */
export function resolveProductUrlSegment(
  locationProduct: LocationProduct,
  locale: string,
  catalogProduct?: Product | null
): string {
  if (catalogProduct) {
    const fromCatalog = getCatalogProductSlug(catalogProduct, locale);
    if (fromCatalog) {
      return fromCatalog;
    }
  }

  const fromLocation = getLocationProductSlug(locationProduct, locale);
  if (fromLocation) {
    return fromLocation;
  }

  return (
    catalogProduct?.productUuid ??
    getLocationProductCatalogUuid(locationProduct) ??
    ""
  );
}

export function resolveCatalogProductForLocation(
  locationProduct: LocationProduct,
  getCatalogProductByUuid: (productUuid: string) => Product | undefined
): Product | undefined {
  const productUuid = getLocationProductCatalogUuid(locationProduct);
  return productUuid ? getCatalogProductByUuid(productUuid) : undefined;
}

export function getProductPageBaseHref(
  productPage: ProductPageLike | undefined,
  languages: LanguageLike[] | undefined,
  fallback = "/et/toode/"
): string {
  let base = productPage
    ? stripPageRouteParam(getPageLocaleUrl(productPage, languages))
    : fallback;
  if (base.charAt(0) !== "/") {
    base = `/${base}`;
  }
  return base;
}

export function buildLocationProductDetailHref(
  baseHref: string,
  locationProduct: LocationProduct,
  locale: string,
  catalogProduct?: Product | null
): string {
  return `${baseHref}${resolveProductUrlSegment(
    locationProduct,
    locale,
    catalogProduct
  )}`;
}

export type ProductDisplayFields = {
  name: string;
  displayName: string;
  shortDescription: string;
  description: string;
  deliveryInfo: string;
  firstFileUrl: string | undefined;
  productUrlSegment: string;
};

function getLocationProductTranslationRecord(
  locationProduct: LocationProduct,
  locale: string
): Record<string, unknown> | undefined {
  const translations = locationProduct.translations;
  if (!translations || typeof translations !== "object") {
    return undefined;
  }
  const translation =
    translations[locale] ?? translations.et ?? translations.en;
  if (!translation || typeof translation !== "object") {
    return undefined;
  }
  return translation as Record<string, unknown>;
}

function readTranslationString(
  record: Record<string, unknown> | undefined,
  ...keys: string[]
): string {
  if (!record) {
    return "";
  }
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return "";
}

export function catalogPageSeoToSnakeCase(
  pageSeo: PageSeo
): Record<string, unknown> {
  const mapped: Record<string, unknown> = {};
  if (pageSeo.seoNoIndex != null) {
    mapped.seo_no_index = pageSeo.seoNoIndex;
  }
  if (pageSeo.seoOgImageUrl) {
    mapped.seo_og_image_url = pageSeo.seoOgImageUrl;
  }
  if (pageSeo.seoTwitterImage) {
    mapped.seo_twitter_image = pageSeo.seoTwitterImage;
  }
  return mapped;
}

export function getCatalogProductPageSeo(
  product: Product,
  locale: string
): Record<string, unknown> | undefined {
  const translation = getCatalogProductTranslation(product, locale);
  const catalogSeo = translation?.pageSeo;
  if (!catalogSeo) {
    return undefined;
  }
  return catalogPageSeoToSnakeCase(catalogSeo);
}

/** Merged page_seo for MetadataComponent (snake_case); catalog overlays location. */
export function resolveProductPageSeo(
  locationProduct: LocationProduct,
  locale: string,
  catalogProduct?: Product | null
): Record<string, unknown> | undefined {
  const locationRecord = getLocationProductTranslationRecord(
    locationProduct,
    locale
  );
  const locationSeo = locationRecord?.page_seo ?? locationRecord?.pageSeo;
  const base =
    locationSeo && typeof locationSeo === "object"
      ? { ...(locationSeo as Record<string, unknown>) }
      : {};

  const catalogTranslation = catalogProduct
    ? getCatalogProductTranslation(catalogProduct, locale)
    : undefined;
  const catalogSeo = catalogTranslation?.pageSeo;
  if (catalogSeo) {
    Object.assign(base, catalogPageSeoToSnakeCase(catalogSeo));
    if (catalogSeo.seoOgImageUrl && !base.seo_twitter_image) {
      base.seo_twitter_image = catalogSeo.seoOgImageUrl;
    }
  }

  return Object.keys(base).length > 0 ? base : undefined;
}

export function getProductDisplayFields(
  locationProduct: LocationProduct,
  locale: string,
  catalogProduct?: Product | null
): ProductDisplayFields {
  const locationRecord = getLocationProductTranslationRecord(
    locationProduct,
    locale
  );
  const catalogTranslation = catalogProduct
    ? getCatalogProductTranslation(catalogProduct, locale)
    : undefined;

  const name =
    catalogTranslation?.name ||
    readTranslationString(locationRecord, "name") ||
    "";
  const displayName =
    catalogTranslation?.displayName ||
    readTranslationString(locationRecord, "display_name", "displayName") ||
    name;
  const shortDescription =
    catalogTranslation?.shortDescription ||
    readTranslationString(
      locationRecord,
      "shortDescription",
      "short_description"
    ) ||
    "";
  const description =
    catalogTranslation?.description ||
    readTranslationString(locationRecord, "description") ||
    "";
  const deliveryInfo =
    catalogTranslation?.deliveryInfo ||
    readTranslationString(locationRecord, "deliveryInfo") ||
    "";

  const catalogImage =
    typeof catalogProduct?.firstFileUrl === "string" &&
    catalogProduct.firstFileUrl.trim()
      ? catalogProduct.firstFileUrl.trim()
      : undefined;
  const locationImage =
    typeof locationProduct.firstFileUrl === "string" &&
    locationProduct.firstFileUrl.trim()
      ? locationProduct.firstFileUrl.trim()
      : undefined;

  return {
    name,
    displayName,
    shortDescription,
    description,
    deliveryInfo,
    firstFileUrl: catalogImage ?? locationImage,
    productUrlSegment: resolveProductUrlSegment(
      locationProduct,
      locale,
      catalogProduct
    ),
  };
}

export function findCatalogProductBySlug(
  catalogProducts: Product[],
  slug: string
): Product | undefined {
  const normalized = slug.trim();
  if (!normalized) {
    return undefined;
  }

  return catalogProducts.find((product) => {
    for (const translation of Object.values(product.translations)) {
      if (translation?.productUrl?.trim() === normalized) {
        return true;
      }
    }
    return false;
  });
}

export function resolveLocationProductFromRouteParam(
  param: string | undefined,
  locationProducts: LocationProduct[],
  catalogProducts: Product[]
): {
  locationProduct: LocationProduct | null;
  catalogProduct: Product | undefined;
} {
  if (!param) {
    return {
      locationProduct: locationProducts.length ? locationProducts[0] : null,
      catalogProduct: undefined,
    };
  }

  const catalogBySlug = findCatalogProductBySlug(catalogProducts, param);
  if (catalogBySlug) {
    const locationProductsByUuid =
      buildLocationProductsByProductUuid(locationProducts);
    const locationProduct =
      findLocationProductForCatalogProduct(
        catalogBySlug,
        locationProductsByUuid,
        locationProducts
      ) ?? null;
    return { locationProduct, catalogProduct: catalogBySlug };
  }

  const byLocationUuid = locationProducts.find(
    (lp) => getLocationProductCatalogUuid(lp) === param
  );
  if (byLocationUuid) {
    const catalogProduct = catalogProducts.find(
      (p) => p.productUuid === param
    );
    return { locationProduct: byLocationUuid, catalogProduct };
  }

  const byCatalogUuid = catalogProducts.find((p) => p.productUuid === param);
  if (byCatalogUuid) {
    const locationProduct =
      locationProducts.find(
        (lp) =>
          getLocationProductCatalogUuid(lp) === byCatalogUuid.productUuid
      ) ??
      findLocationProductForCatalogProduct(
        byCatalogUuid,
        buildLocationProductsByProductUuid(locationProducts),
        locationProducts
      ) ??
      null;
    return { locationProduct, catalogProduct: byCatalogUuid };
  }

  const byLocationSlug = locationProducts.find((lp) => {
    for (const translation of Object.values(lp.translations ?? {})) {
      if (!translation || typeof translation !== "object") {
        continue;
      }
      const record = translation as Record<string, unknown>;
      const url = record.product_url ?? record.productUrl;
      if (url === param) {
        return true;
      }
    }
    return false;
  });
  if (byLocationSlug) {
    const catalogProduct = catalogProducts.find(
      (p) => p.productUuid === getLocationProductCatalogUuid(byLocationSlug)
    );
    return { locationProduct: byLocationSlug, catalogProduct };
  }

  return { locationProduct: null, catalogProduct: undefined };
}

export function locationProductMatchesSearchTerm(
  locationProduct: LocationProduct,
  locale: string,
  searchTerm: string,
  catalogProduct?: Product | null
): boolean {
  const term = searchTerm.trim().toLowerCase();
  if (!term) {
    return true;
  }

  const display = getProductDisplayFields(
    locationProduct,
    locale,
    catalogProduct
  );
  const parts = [
    display.name,
    display.displayName,
    display.shortDescription,
    display.description,
    display.productUrlSegment,
  ];

  if (catalogProduct?.productCodes?.length) {
    for (const code of catalogProduct.productCodes) {
      parts.push(code.name, code.value);
    }
  }

  return parts.some(
    (part) => typeof part === "string" && part.toLowerCase().includes(term)
  );
}

export function filterLocationProductsBySearchTerm(
  locationProducts: LocationProduct[],
  locale: string,
  searchTerm: string,
  getCatalogProductByUuid: (productUuid: string) => Product | undefined
): LocationProduct[] {
  return locationProducts.filter((locationProduct) =>
    locationProductMatchesSearchTerm(
      locationProduct,
      locale,
      searchTerm,
      resolveCatalogProductForLocation(
        locationProduct,
        getCatalogProductByUuid
      )
    )
  );
}

export function catalogProductHasCategoryUuid(
  product: Product,
  categoryUuid: string
): boolean {
  return (
    product.productCategories?.some((c) => c.uuid === categoryUuid) ?? false
  );
}

export function getLocationProductCatalogUuid(
  locationProduct: LocationProduct
): string | undefined {
  const nested = locationProduct.product;
  if (nested && typeof nested === "object") {
    const record = nested as Record<string, unknown>;
    if (typeof record.uuid === "string") {
      return record.uuid;
    }
    if (typeof record.productUuid === "string") {
      return record.productUuid;
    }
  }

  const root = locationProduct as unknown as Record<string, unknown>;
  if (typeof root.productUuid === "string") {
    return root.productUuid;
  }

  return undefined;
}

export function isListableLocationProduct(
  locationProduct: LocationProduct
): boolean {
  return !locationProduct.product?.only_variation;
}

export function selectListableLocationProducts(
  locationProducts: LocationProduct[]
): LocationProduct[] {
  return locationProducts.filter(isListableLocationProduct);
}

export function buildCatalogProductMap(
  products: Product[]
): Map<string, Product> {
  const map = new Map<string, Product>();
  for (const product of products) {
    map.set(product.productUuid, product);
  }
  return map;
}

export function buildLocationProductsByProductUuid(
  locationProducts: LocationProduct[]
): Map<string, LocationProduct> {
  const map = new Map<string, LocationProduct>();

  for (const locationProduct of locationProducts) {
    if (!isListableLocationProduct(locationProduct)) {
      continue;
    }

    const productUuid = getLocationProductCatalogUuid(locationProduct);
    if (!productUuid || map.has(productUuid)) {
      continue;
    }

    map.set(productUuid, locationProduct);
  }

  return map;
}

function catalogProductUrls(product: Product): Set<string> {
  const urls = new Set<string>();
  for (const translation of Object.values(product.translations)) {
    const url = translation?.productUrl;
    if (url) {
      urls.add(url);
    }
  }
  return urls;
}

function locationProductMatchesCatalogUrls(
  locationProduct: LocationProduct,
  urls: Set<string>
): boolean {
  const translations = locationProduct.translations;
  if (!translations || typeof translations !== "object") {
    return false;
  }

  for (const translation of Object.values(translations)) {
    if (!translation || typeof translation !== "object") {
      continue;
    }
    const record = translation as Record<string, unknown>;
    const slug = record.product_url ?? record.productUrl;
    if (typeof slug === "string" && urls.has(slug)) {
      return true;
    }
  }

  return false;
}

export function findLocationProductForCatalogProduct(
  catalogProduct: Product,
  locationProductsByProductUuid: Map<string, LocationProduct>,
  locationProducts: LocationProduct[]
): LocationProduct | undefined {
  const fromUuid = locationProductsByProductUuid.get(catalogProduct.productUuid);
  if (fromUuid) {
    return fromUuid;
  }

  const urls = catalogProductUrls(catalogProduct);
  if (!urls.size) {
    return undefined;
  }

  return locationProducts.find(
    (locationProduct) =>
      isListableLocationProduct(locationProduct) &&
      locationProductMatchesCatalogUrls(locationProduct, urls)
  );
}

export type HydratedCatalogListItem = {
  catalogProduct: Product;
  locationProduct: LocationProduct | null;
};

export function hydrateCatalogProductsForListing(
  catalogProducts: Product[],
  locationProducts: LocationProduct[]
): HydratedCatalogListItem[] {
  const locationProductsByProductUuid =
    buildLocationProductsByProductUuid(locationProducts);

  return selectListableCatalogProducts(catalogProducts).map((catalogProduct) => ({
    catalogProduct,
    locationProduct:
      findLocationProductForCatalogProduct(
        catalogProduct,
        locationProductsByProductUuid,
        locationProducts
      ) ?? null,
  }));
}

export function getHydratedListLocationProducts(
  items: HydratedCatalogListItem[]
): LocationProduct[] {
  return items
    .map((item) => item.locationProduct)
    .filter((item): item is LocationProduct => item != null);
}

/**
 * Preferred product grid source: catalog order from products.json when loaded,
 * each row joined to a location product for cart/links. Falls back to
 * loadLocationProducts-only list when catalog is empty or nothing joins.
 */
export function resolveCatalogListingLocationProducts(
  catalogProducts: Product[],
  locationProducts: LocationProduct[]
): LocationProduct[] {
  if (!catalogProducts.length) {
    return selectListableLocationProducts(locationProducts);
  }

  const hydrated = hydrateCatalogProductsForListing(
    catalogProducts,
    locationProducts
  );
  const joined = getHydratedListLocationProducts(hydrated);

  if (joined.length > 0) {
    return joined;
  }

  return selectListableLocationProducts(locationProducts);
}
